// src/hooks/useCitySearch.js
import { useState, useCallback, useRef, useMemo } from 'react';

const useCitySearch = (cities = []) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showResults, setShowResults] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  
  const searchTimeoutRef = useRef(null);
  
  // Simple fuzzy search function
  const fuzzyMatch = useCallback((text, query) => {
    if (!query) return { score: 0, matches: false };
    
    const textLower = text.toLowerCase();
    const queryLower = query.toLowerCase();

    // Exact match (case-insensitive) gets the highest score
    if (textLower === queryLower) {
      return { score: 300, matches: true };
    }

    // Prefix match (city name starts with query) gets second highest score
    if (textLower.startsWith(queryLower)) {
      return { score: 200, matches: true };
    }
    
    // Direct substring match (query appears anywhere in name)
    if (textLower.includes(queryLower)) {
      return { score: 100, matches: true };
    }
    
    // Character-by-character fuzzy matching
    let textIndex = 0;
    let queryIndex = 0;
    let matches = 0;
    
    while (textIndex < textLower.length && queryIndex < queryLower.length) {
      if (textLower[textIndex] === queryLower[queryIndex]) {
        matches++;
        queryIndex++;
      }
      textIndex++;
    }
    
    if (queryIndex === queryLower.length) {
      const score = (matches / queryLower.length) * 50; // Max 50 for fuzzy match
      return { score, matches: true };
    }
    
    return { score: 0, matches: false };
  }, []);
  
  // Search and sort cities with fuzzy matching
  const filteredCities = useMemo(() => {
    if (!searchTerm.trim()) return [];
    
    const results = cities.map(city => {
      const cityName = city.name || '';
      
      const cityMatch = fuzzyMatch(cityName, searchTerm);
      
      return {
        ...city,
        score: cityMatch.score,
        hasMatch: cityMatch.matches
      };
    })
    .filter(city => city.hasMatch)
    .sort((a, b) => b.score - a.score)
    .slice(0, 10); // Limit to 10 results
    
    return results;
  }, [cities, searchTerm, fuzzyMatch]);
  
  // Handle search input change
  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    setSelectedIndex(-1);
    setShowResults(true);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      if (!value.trim()) {
        setShowResults(false);
      }
    }, 150);
  }, []);
  
  // Handle keyboard navigation
  const handleKeyDown = useCallback((event) => {
    if (!showResults || filteredCities.length === 0) return null;
    
    switch (event.key) {
      case 'ArrowDown':
      case 'Tab':
        event.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredCities.length - 1 ? prev + 1 : 0
        );
        break;
        
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : filteredCities.length - 1);
        break;
        
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filteredCities.length) {
          return filteredCities[selectedIndex];
        }
        break;
        
      case 'Escape':
        event.preventDefault();
        setShowResults(false);
        setSelectedIndex(-1);
        break;
        
      default:
        break;
    }
    
    return null;
  }, [showResults, filteredCities, selectedIndex]);
  
  // Handle city selection
  const selectCity = useCallback((city) => {
    setSelectedCity(city);
    setSearchTerm(city.name);
    setShowResults(false);
    setSelectedIndex(-1);
    return city;
  }, []);
  
  // Clear search
  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setSelectedCity(null);
    setShowResults(false);
    setSelectedIndex(-1);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, []);
  
  // Hide search results
  const hideResults = useCallback(() => {
    setShowResults(false);
    setSelectedIndex(-1);
  }, []);
  
  // Show search results
  const showResultsDropdown = useCallback(() => {
    if (searchTerm.trim()) {
      setShowResults(true);
    }
  }, [searchTerm]);
  
  // Format city display
  const formatCityDisplay = useCallback((city) => {
    return `${city.name} - ${city.District?.name}, ${city.District?.Province?.name}`;
  }, []);
  
  return {
    // Search state
    searchTerm,
    filteredCities,
    selectedIndex,
    showResults,
    selectedCity,
    
    // Handlers
    handleSearchChange,
    handleKeyDown,
    selectCity,
    clearSearch,
    hideResults,
    showResultsDropdown,
    
    // Utilities
    formatCityDisplay
  };
};

export default useCitySearch;